import React, { useState, useRef, useEffect } from 'react';
import { Filter, X, MapPin, ShieldCheck, FileText, Folder, Building, Calendar, ChevronDown, Search, Tags } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AccordionPanel from './AccordionPanel';
import { parseMultiSelect, serializeMultiSelect } from '../utils/filtersApi';

const INSURANCE_FILTER_SECTIONS = [
  'company',
  'state',
  'status',
  'type',
  'toi',
  'response',
  'subtoi',
  'submissionDates',
  'dispositionDates',
];

const DEFAULT_FILTER_DEFAULTS = {
  state: 'All states',
  status: 'All statuses',
  type: 'All filing types',
  toi: 'All TOI',
  subToi: 'All Sub-TOI',
  company: 'All companies',
  response: 'All',
  xbrlTag: 'All XBRL tags',
};

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="custom-date-input-wrapper" onClick={onClick} ref={ref}>
    <input
      type="text"
      readOnly
      value={value}
      placeholder={placeholder || 'MM-DD-YYYY'}
      className="custom-date-input-field"
    />
  </div>
));

export default function FiltersDrawer({
  isOpen,
  onClose,
  initialOpenSection, // Force auto-expansion of specific accordion key ('state', 'type', etc.)
  filterState: propFilterState,
  setFilterState: propSetFilterState,
  filterStatus: propFilterStatus,
  setFilterStatus: propSetFilterStatus,
  filterType: propFilterType,
  setFilterType: propSetFilterType,
  filterToi: propFilterToi,
  setFilterToi: propSetFilterToi,
  filterSubToi: propFilterSubToi,
  setFilterSubToi: propSetFilterSubToi,
  filterCompany: propFilterCompany,
  setFilterCompany: propSetFilterCompany,
  filterXbrlTag: propFilterXbrlTag,
  setFilterXbrlTag: propSetFilterXbrlTag,
  filterResponse: propFilterResponse,
  setFilterResponse: propSetFilterResponse,
  options = { states: [], filingTypes: [], companies: [], xbrlTags: [] },
  statuses = [],
  tois = [],
  subTois = [],
  isOurFilings = false,
  isCompetitors = false,
  mappedCompetitors = [], // Intel Axis competitors from scope-config (intel_axis_competitors), not all SERFF companies
  setCurrentPage,
  isInsights = false,
  subStart: propSubStart,
  setSubStart: propSetSubStart,
  subEnd: propSubEnd,
  setSubEnd: propSetSubEnd,
  tempSubRange,
  setTempSubRange,
  dispStart: propDispStart,
  setDispStart: propSetDispStart,
  dispEnd: propDispEnd,
  setDispEnd: propSetDispEnd,
  tempDispRange,
  setTempDispRange,
  submissionDateTitle = 'Submission dates',
  dispositionDateTitle = 'Disposition dates',
  showSubmissionDates = true,
  visibleSections = null,
  filterDefaults: filterDefaultsProp = null,
  filingTypeTitle = 'Filing Type',
  filingTypeSearchPlaceholder = 'Search filing types',
  xbrlTagTitle = 'XBRL Tag',
  xbrlTagSearchPlaceholder = 'Search XBRL tags',
  optionLabelForXbrlTag = null,
  resetButtonLabel = 'Reset All',
  applyButtonLabel = 'Apply Filters',
}) {
  const activeSections = new Set(visibleSections ?? INSURANCE_FILTER_SECTIONS);
  const showSection = (section) => activeSections.has(section);
  const defaults = { ...DEFAULT_FILTER_DEFAULTS, ...filterDefaultsProp };
  const companyDefault = defaults.company;
  const stateDefault = defaults.state;
  const statusDefault = defaults.status;
  const typeDefault = defaults.type;
  const toiDefault = defaults.toi;
  const subToiDefault = defaults.subToi;
  const responseDefault = defaults.response;
  const xbrlTagDefault = defaults.xbrlTag;
  const xbrlTags = options.xbrlTags ?? [];
  const formatXbrlTagLabel = optionLabelForXbrlTag || ((tag) => tag);

  const [filterState, setFilterState] = useState(propFilterState);
  const [filterStatus, setFilterStatus] = useState(propFilterStatus);
  const [filterType, setFilterType] = useState(propFilterType);
  const [filterToi, setFilterToi] = useState(propFilterToi);
  const [filterSubToi, setFilterSubToi] = useState(propFilterSubToi);
  const [filterCompany, setFilterCompany] = useState(propFilterCompany);
  const [filterXbrlTag, setFilterXbrlTag] = useState(propFilterXbrlTag);
  const [filterResponse, setFilterResponse] = useState(propFilterResponse);
  const [subStart, setSubStart] = useState(propSubStart);
  const [subEnd, setSubEnd] = useState(propSubEnd);
  const [dispStart, setDispStart] = useState(propDispStart);
  const [dispEnd, setDispEnd] = useState(propDispEnd);

  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const datePickerRef = useRef(null);
  const dispDatePickerRef = useRef(null);
  const showDispositionDates = Boolean(propSetDispStart && propSetDispEnd);

  useEffect(() => {
    if (isOpen) {
      setFilterState(propFilterState);
      setFilterStatus(propFilterStatus);
      setFilterType(propFilterType);
      setFilterToi(propFilterToi);
      setFilterSubToi(propFilterSubToi);
      setFilterCompany(propFilterCompany);
      if (propSetFilterXbrlTag) setFilterXbrlTag(propFilterXbrlTag);
      setFilterResponse(propFilterResponse);
      setSubStart(propSubStart);
      setSubEnd(propSubEnd);
      if (setTempSubRange) {
        setTempSubRange([propSubStart, propSubEnd]);
      }
      if (showDispositionDates) {
        setDispStart(propDispStart);
        setDispEnd(propDispEnd);
        if (setTempDispRange) {
          setTempDispRange([propDispStart, propDispEnd]);
        }
      }

      // Core expansion link:
      if (initialOpenSection) {
        setOpenFilter(initialOpenSection);
      } else {
        setOpenFilter(null);
      }
    }
  }, [isOpen, propFilterState, propFilterStatus, propFilterType, propFilterToi, propFilterSubToi, propFilterCompany, propFilterXbrlTag, propFilterResponse, propSubStart, propSubEnd, propDispStart, propDispEnd, initialOpenSection, setTempSubRange, setTempDispRange, showDispositionDates, propSetFilterXbrlTag]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.accordion-item') && !e.target.closest('.react-datepicker')) {
        setOpenFilter(null);
        setFilterSearch('');
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen]);

  const getSelected = (currentVal, defaultVal, catalog = null) =>
    parseMultiSelect(currentVal, defaultVal, catalog);

  const countSelected = (currentVal, defaultVal, catalog = null) =>
    (currentVal === defaultVal ? 0 : getSelected(currentVal, defaultVal, catalog).length);

  const isOptionSelected = (currentVal, defaultVal, option, catalog = null) =>
    currentVal !== defaultVal && getSelected(currentVal, defaultVal, catalog).includes(option);

  const handleRemoveFilter = (currentValue, valueToRemove, defaultValue, setValueFn, catalog = null) => {
    if (!currentValue || currentValue === defaultValue) return;
    const values = getSelected(currentValue, defaultValue, catalog).filter((v) => v !== valueToRemove);
    if (values.length === 0) {
      setValueFn(defaultValue);
    } else {
      setValueFn(serializeMultiSelect(values));
    }
    if (setCurrentPage) setCurrentPage(1);
  };

  const toggleMultiSelectFilter = (currentVal, option, defaultVal, setter, catalog = null) => {
    if (option === defaultVal) {
      setter(defaultVal);
    } else {
      let selected = currentVal === defaultVal ? [] : getSelected(currentVal, defaultVal, catalog);
      if (selected.includes(option)) {
        selected = selected.filter((x) => x !== option);
      } else {
        selected.push(option);
      }
      if (selected.length === 0) {
        setter(defaultVal);
      } else {
        setter(serializeMultiSelect(selected));
      }
    }
    if (setCurrentPage) setCurrentPage(1);
  };

  const handleSelectAllToggle = (currentVal, optionsList, defaultVal, setter, catalog = null) => {
    const catalogForParse = catalog || optionsList;
    const selected = currentVal === defaultVal ? [] : getSelected(currentVal, defaultVal, catalogForParse);
    if (selected.length === optionsList.length) {
      setter(defaultVal);
    } else {
      setter(serializeMultiSelect(optionsList));
    }
    if (setCurrentPage) setCurrentPage(1);
  };

  const renderBadges = (currentValue, defaultValue, setValueFn, catalog = null, formatLabel = (item) => item) => {
    if (!currentValue || currentValue === defaultValue) return null;
    const items = getSelected(currentValue, defaultValue, catalog);
    return (
      <div className="filters-badge-container">
        {items.map((item) => (
          <div key={item} className="filters-badge">
            <span>{formatLabel(item)}</span>
            <button
              onClick={() => handleRemoveFilter(currentValue, item, defaultValue, setValueFn, catalog)}
              className="filters-badge-cancel"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`details-drawer filters-details-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3 className="drawer-title filters-drawer-title-wrap">
            <Filter size={18} />
            <span>Filters</span>
          </h3>
          <button className="drawer-close filters-drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="drawer-body filters-drawer-body">
          {/* Company Filter Accordion */}
          {showSection('company') && !isOurFilings && (
            <div className="accordion-item">
              <div
                className="accordion-header"
                onClick={() => {
                  setOpenFilter(openFilter === 'company' ? null : 'company');
                  setFilterSearch('');
                }}
              >
                <div className="accordion-header-left">
                  <Building size={18} className="accordion-header-icon" />
                  <span className="accordion-header-title">Company</span>
                </div>
                <div className="accordion-header-right">
                  {filterCompany !== companyDefault && (
                    <span
                      key={countSelected(filterCompany, companyDefault, isCompetitors ? mappedCompetitors : options.companies)}
                      className="accordion-selected-count"
                    >
                      {countSelected(filterCompany, companyDefault, isCompetitors ? mappedCompetitors : options.companies)}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`accordion-chevron ${openFilter === 'company' ? 'is-open' : ''}`}
                  />
                </div>
              </div>

              <AccordionPanel isOpen={openFilter === 'company'}>
                  <div className="custom-dropdown-header accordion-search-container">
                    <div className="custom-dropdown-search-wrap">
                      <Search size={16} className="custom-dropdown-search-icon" />
                      <input
                        type="text"
                        className="custom-dropdown-search-input accordion-search-input"
                        placeholder="Search companies"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  {isCompetitors ? (
                    <>
                      <div
                        className="custom-dropdown-item select-all"
                        onClick={() => {
                          handleSelectAllToggle(filterCompany, mappedCompetitors, companyDefault, setFilterCompany, mappedCompetitors);
                        }}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={filterCompany !== companyDefault && countSelected(filterCompany, companyDefault, mappedCompetitors) === mappedCompetitors.length}
                          readOnly
                        />
                        <span>Select all</span>
                      </div>
                      <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                        {mappedCompetitors.length === 0 && (
                          <div className="custom-dropdown-item" style={{ cursor: 'default', opacity: 0.7 }}>
                            <span>No mapped competitors configured</span>
                          </div>
                        )}
                        {mappedCompetitors.filter(c => c.toLowerCase().includes(filterSearch.toLowerCase())).map(c => {
                          const isChecked = isOptionSelected(filterCompany, companyDefault, c, mappedCompetitors);
                          return (
                            <div
                              key={c}
                              className="custom-dropdown-item"
                              onClick={() => toggleMultiSelectFilter(filterCompany, c, companyDefault, setFilterCompany, mappedCompetitors)}
                            >
                              <input
                                type="checkbox"
                                className="custom-dropdown-checkbox"
                                checked={isChecked}
                                readOnly
                              />
                              <span>{c}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="custom-dropdown-item select-all"
                        onClick={() => {
                          handleSelectAllToggle(filterCompany, options.companies, companyDefault, setFilterCompany, options.companies);
                        }}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={filterCompany !== companyDefault && countSelected(filterCompany, companyDefault, options.companies) === options.companies.length}
                          readOnly
                        />
                        <span>Select all</span>
                      </div>
                      <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                        {options.companies.filter(c => c.toLowerCase().includes(filterSearch.toLowerCase())).map(c => {
                          const isChecked = isOptionSelected(filterCompany, companyDefault, c, options.companies);
                          return (
                            <div
                              key={c}
                              className="custom-dropdown-item"
                              onClick={() => toggleMultiSelectFilter(filterCompany, c, companyDefault, setFilterCompany, options.companies)}
                            >
                              <input
                                type="checkbox"
                                className="custom-dropdown-checkbox"
                                checked={isChecked}
                                readOnly
                              />
                              <span>{c}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {renderBadges(
                    filterCompany,
                    companyDefault,
                    setFilterCompany,
                    isCompetitors ? mappedCompetitors : options.companies,
                  )}
              </AccordionPanel>
            </div>
          )}

          {/* State Filter Accordion */}
          {showSection('state') && (
          <div className="accordion-item">
            <div
              className="accordion-header"
              onClick={() => {
                setOpenFilter(openFilter === 'state' ? null : 'state');
                setFilterSearch('');
              }}
            >
              <div className="accordion-header-left">
                <MapPin size={18} className="accordion-header-icon" />
                <span className="accordion-header-title">State</span>
              </div>
              <div className="accordion-header-right">
                {filterState !== stateDefault && (
                  <span key={countSelected(filterState, stateDefault, options.states)} className="accordion-selected-count">
                    {countSelected(filterState, stateDefault, options.states)}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`accordion-chevron ${openFilter === 'state' ? 'is-open' : ''}`}
                />
              </div>
            </div>

            <AccordionPanel isOpen={openFilter === 'state'}>
                <div className="custom-dropdown-header accordion-search-container">
                  <div className="custom-dropdown-search-wrap">
                    <Search size={16} className="custom-dropdown-search-icon" />
                    <input
                      type="text"
                      className="custom-dropdown-search-input accordion-search-input"
                      placeholder="Search states"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div
                  className="custom-dropdown-item select-all"
                  onClick={() => {
                    handleSelectAllToggle(filterState, options.states, stateDefault, setFilterState, options.states);
                  }}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={filterState !== stateDefault && countSelected(filterState, stateDefault, options.states) === options.states.length}
                    readOnly
                  />
                  <span>Select all</span>
                </div>
                <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                  {options.states.filter(s => s.toLowerCase().includes(filterSearch.toLowerCase())).map(s => {
                    const isChecked = isOptionSelected(filterState, stateDefault, s, options.states);
                    return (
                      <div
                        key={s}
                        className="custom-dropdown-item"
                        onClick={() => toggleMultiSelectFilter(filterState, s, stateDefault, setFilterState, options.states)}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={isChecked}
                          readOnly
                        />
                        <span>{s}</span>
                      </div>
                    );
                  })}
                </div>
                {renderBadges(filterState, stateDefault, setFilterState, options.states)}
              </AccordionPanel>
          </div>
          )}

          {/* Status Filter Accordion */}
          {showSection('status') && (
          <div className="accordion-item">
            <div
              className="accordion-header"
              onClick={() => {
                setOpenFilter(openFilter === 'status' ? null : 'status');
                setFilterSearch('');
              }}
            >
              <div className="accordion-header-left">
                <ShieldCheck size={18} className="accordion-header-icon" />
                <span className="accordion-header-title">SERFF Status</span>
              </div>
              <div className="accordion-header-right">
                {filterStatus !== statusDefault && (
                  <span key={countSelected(filterStatus, statusDefault, statuses.filter((s) => s !== statusDefault))} className="accordion-selected-count">
                    {countSelected(filterStatus, statusDefault, statuses.filter((s) => s !== statusDefault))}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`accordion-chevron ${openFilter === 'status' ? 'is-open' : ''}`}
                />
              </div>
            </div>

            <AccordionPanel isOpen={openFilter === 'status'}>
                <div className="custom-dropdown-header accordion-search-container">
                  <div className="custom-dropdown-search-wrap">
                    <Search size={16} className="custom-dropdown-search-icon" />
                    <input
                      type="text"
                      className="custom-dropdown-search-input accordion-search-input"
                      placeholder="Search statuses"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div
                  className="custom-dropdown-item select-all"
                  onClick={() => {
                    const optionsList = statuses.filter(s => s !== statusDefault);
                    handleSelectAllToggle(filterStatus, optionsList, statusDefault, setFilterStatus, optionsList);
                  }}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={filterStatus !== statusDefault && countSelected(filterStatus, statusDefault, statuses.filter((s) => s !== statusDefault)) === statuses.filter(s => s !== statusDefault).length}
                    readOnly
                  />
                  <span>Select all</span>
                </div>
                <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                  {statuses.filter(s => s !== statusDefault && s.toLowerCase().includes(filterSearch.toLowerCase())).map(s => {
                    const statusOptions = statuses.filter((st) => st !== statusDefault);
                    const isChecked = isOptionSelected(filterStatus, statusDefault, s, statusOptions);
                    return (
                      <div
                        key={s}
                        className="custom-dropdown-item"
                        onClick={() => toggleMultiSelectFilter(filterStatus, s, statusDefault, setFilterStatus, statuses.filter((st) => st !== statusDefault))}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={isChecked}
                          readOnly
                        />
                        <span>{s}</span>
                      </div>
                    );
                  })}
                </div>
                {renderBadges(filterStatus, statusDefault, setFilterStatus, statuses.filter((s) => s !== statusDefault))}
              </AccordionPanel>
          </div>
          )}

          {/* Filing Type Filter Accordion */}
          {showSection('type') && (
          <div className="accordion-item">
            <div
              className="accordion-header"
              onClick={() => {
                setOpenFilter(openFilter === 'type' ? null : 'type');
                setFilterSearch('');
              }}
            >
              <div className="accordion-header-left">
                <FileText size={18} className="accordion-header-icon" />
                <span className="accordion-header-title">{filingTypeTitle}</span>
              </div>
              <div className="accordion-header-right">
                {filterType !== typeDefault && (
                  <span key={countSelected(filterType, typeDefault, options.filingTypes)} className="accordion-selected-count">
                    {countSelected(filterType, typeDefault, options.filingTypes)}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`accordion-chevron ${openFilter === 'type' ? 'is-open' : ''}`}
                />
              </div>
            </div>

            <AccordionPanel isOpen={openFilter === 'type'}>
                <div className="custom-dropdown-header accordion-search-container">
                  <div className="custom-dropdown-search-wrap">
                    <Search size={16} className="custom-dropdown-search-icon" />
                    <input
                      type="text"
                      className="custom-dropdown-search-input accordion-search-input"
                      placeholder={filingTypeSearchPlaceholder}
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div
                  className="custom-dropdown-item select-all"
                  onClick={() => {
                    handleSelectAllToggle(filterType, options.filingTypes, typeDefault, setFilterType, options.filingTypes);
                  }}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={filterType !== typeDefault && countSelected(filterType, typeDefault, options.filingTypes) === options.filingTypes.length}
                    readOnly
                  />
                  <span>Select all</span>
                </div>
                <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                  {options.filingTypes.filter(t => t.toLowerCase().includes(filterSearch.toLowerCase())).map(t => {
                    const isChecked = isOptionSelected(filterType, typeDefault, t, options.filingTypes);
                    return (
                      <div
                        key={t}
                        className="custom-dropdown-item"
                        onClick={() => toggleMultiSelectFilter(filterType, t, typeDefault, setFilterType, options.filingTypes)}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={isChecked}
                          readOnly
                        />
                        <span>{t}</span>
                      </div>
                    );
                  })}
                </div>
                {renderBadges(filterType, typeDefault, setFilterType, options.filingTypes)}
              </AccordionPanel>
          </div>
          )}

          {/* XBRL Tag Filter Accordion */}
          {showSection('xbrlTag') && propSetFilterXbrlTag && (
          <div className="accordion-item">
            <div
              className="accordion-header"
              onClick={() => {
                setOpenFilter(openFilter === 'xbrlTag' ? null : 'xbrlTag');
                setFilterSearch('');
              }}
            >
              <div className="accordion-header-left">
                <Tags size={18} className="accordion-header-icon" />
                <span className="accordion-header-title">{xbrlTagTitle}</span>
              </div>
              <div className="accordion-header-right">
                {filterXbrlTag !== xbrlTagDefault && (
                  <span key={countSelected(filterXbrlTag, xbrlTagDefault, xbrlTags)} className="accordion-selected-count">
                    {countSelected(filterXbrlTag, xbrlTagDefault, xbrlTags)}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`accordion-chevron ${openFilter === 'xbrlTag' ? 'is-open' : ''}`}
                />
              </div>
            </div>

            <AccordionPanel isOpen={openFilter === 'xbrlTag'}>
                <div className="custom-dropdown-header accordion-search-container">
                  <div className="custom-dropdown-search-wrap">
                    <Search size={16} className="custom-dropdown-search-icon" />
                    <input
                      type="text"
                      className="custom-dropdown-search-input accordion-search-input"
                      placeholder={xbrlTagSearchPlaceholder}
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div
                  className="custom-dropdown-item select-all"
                  onClick={() => {
                    handleSelectAllToggle(filterXbrlTag, xbrlTags, xbrlTagDefault, setFilterXbrlTag, xbrlTags);
                  }}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={filterXbrlTag !== xbrlTagDefault && countSelected(filterXbrlTag, xbrlTagDefault, xbrlTags) === xbrlTags.length}
                    readOnly
                  />
                  <span>Select all</span>
                </div>
                <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                  {xbrlTags.filter((tag) => formatXbrlTagLabel(tag).toLowerCase().includes(filterSearch.toLowerCase())).map((tag) => {
                    const isChecked = isOptionSelected(filterXbrlTag, xbrlTagDefault, tag, xbrlTags);
                    return (
                      <div
                        key={tag}
                        className="custom-dropdown-item"
                        onClick={() => toggleMultiSelectFilter(filterXbrlTag, tag, xbrlTagDefault, setFilterXbrlTag, xbrlTags)}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={isChecked}
                          readOnly
                        />
                        <span>{formatXbrlTagLabel(tag)}</span>
                      </div>
                    );
                  })}
                </div>
                {renderBadges(filterXbrlTag, xbrlTagDefault, setFilterXbrlTag, xbrlTags, formatXbrlTagLabel)}
              </AccordionPanel>
          </div>
          )}

          {/* TOI Filter Accordion */}
          {showSection('toi') && (
          <div className="accordion-item">
            <div
              className="accordion-header"
              onClick={() => {
                setOpenFilter(openFilter === 'toi' ? null : 'toi');
                setFilterSearch('');
              }}
            >
              <div className="accordion-header-left">
                <Folder size={18} className="accordion-header-icon" />
                <span className="accordion-header-title">Type of Insurance (TOI)</span>
              </div>
              <div className="accordion-header-right">
                {filterToi !== toiDefault && (
                  <span key={countSelected(filterToi, toiDefault, tois)} className="accordion-selected-count">
                    {countSelected(filterToi, toiDefault, tois)}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`accordion-chevron ${openFilter === 'toi' ? 'is-open' : ''}`}
                />
              </div>
            </div>

            <AccordionPanel isOpen={openFilter === 'toi'}>
                <div className="custom-dropdown-header accordion-search-container">
                  <div className="custom-dropdown-search-wrap">
                    <Search size={16} className="custom-dropdown-search-icon" />
                    <input
                      type="text"
                      className="custom-dropdown-search-input accordion-search-input"
                      placeholder="Search TOI"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div
                  className="custom-dropdown-item select-all"
                  onClick={() => {
                    handleSelectAllToggle(filterToi, tois, toiDefault, setFilterToi, tois);
                  }}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={filterToi !== toiDefault && countSelected(filterToi, toiDefault, tois) === tois.length}
                    readOnly
                  />
                  <span>Select all</span>
                </div>
                <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                  {tois.filter(t => t.toLowerCase().includes(filterSearch.toLowerCase())).map(t => {
                    const isChecked = isOptionSelected(filterToi, toiDefault, t, tois);
                    return (
                      <div
                        key={t}
                        className="custom-dropdown-item"
                        onClick={() => toggleMultiSelectFilter(filterToi, t, toiDefault, setFilterToi, tois)}
                      >
                        <input
                          type="checkbox"
                          className="custom-dropdown-checkbox"
                          checked={isChecked}
                          readOnly
                        />
                        <span>{t}</span>
                      </div>
                    );
                  })}
                </div>
                {renderBadges(filterToi, toiDefault, setFilterToi, tois)}
              </AccordionPanel>
          </div>
          )}

          {showSection('response') && propFilterResponse !== undefined && (
            <div className="accordion-item">
              <div
                className="accordion-header"
                style={{ borderBottom: openFilter === 'response' ? '1px solid var(--border2)' : 'none' }}
                onClick={() => {
                  setOpenFilter(openFilter === 'response' ? null : 'response');
                  setFilterSearch('');
                }}
              >
                <div className="accordion-header-left">
                  <ShieldCheck size={18} className="accordion-header-icon" />
                  <span className="accordion-header-title">Response</span>
                </div>
                <div className="accordion-header-right">
                  {filterResponse !== 'All' && (
                    <span key={countSelected(filterResponse, 'All', ['Responded', 'No response filed'])} className="accordion-selected-count">
                      {countSelected(filterResponse, 'All', ['Responded', 'No response filed'])}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`accordion-chevron ${openFilter === 'response' ? 'is-open' : ''}`}
                  />
                </div>
              </div>

              <AccordionPanel isOpen={openFilter === 'response'}>
                  <div
                    className="custom-dropdown-item select-all"
                    onClick={() => {
                      handleSelectAllToggle(filterResponse, ['Responded', 'No response filed'], 'All', setFilterResponse, ['Responded', 'No response filed']);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="custom-dropdown-checkbox"
                      checked={filterResponse !== 'All' && countSelected(filterResponse, 'All', ['Responded', 'No response filed']) === 2}
                      readOnly
                    />
                    <span>Select all</span>
                  </div>
                  <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                    {['Responded', 'No response filed'].map(opt => {
                      const responseOptions = ['Responded', 'No response filed'];
                      const isChecked = isOptionSelected(filterResponse, 'All', opt, responseOptions);
                      return (
                        <div
                          key={opt}
                          className="custom-dropdown-item"
                          onClick={() => toggleMultiSelectFilter(filterResponse, opt, 'All', setFilterResponse, ['Responded', 'No response filed'])}
                        >
                          <input
                            type="checkbox"
                            className="custom-dropdown-checkbox"
                            checked={isChecked}
                            readOnly
                          />
                          <span>{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                  {renderBadges(filterResponse, 'All', setFilterResponse, ['Responded', 'No response filed'])}
                </AccordionPanel>
            </div>
          )}

          {/* Sub-TOI Filter Accordion (Only visible when subTois populated) */}
          {showSection('subtoi') && propSetFilterSubToi && subTois.length > 0 && (
            <div className="accordion-item">
              <div
                className="accordion-header"
                onClick={() => {
                  setOpenFilter(openFilter === 'subtoi' ? null : 'subtoi');
                  setFilterSearch('');
                }}
              >
                <div className="accordion-header-left">
                  <Folder size={18} className="accordion-header-icon" />
                  <span className="accordion-header-title">Sub-TOI</span>
                </div>
                <div className="accordion-header-right">
                  {filterSubToi && filterSubToi !== 'All Sub-TOI' && (
                    <span key={countSelected(filterSubToi, 'All Sub-TOI', subTois)} className="accordion-selected-count">
                      {countSelected(filterSubToi, 'All Sub-TOI', subTois)}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`accordion-chevron ${openFilter === 'subtoi' ? 'is-open' : ''}`}
                  />
                </div>
              </div>

              <AccordionPanel isOpen={openFilter === 'subtoi'}>
                  <div className="custom-dropdown-header accordion-search-container">
                    <div className="custom-dropdown-search-wrap">
                      <Search size={16} className="custom-dropdown-search-icon" />
                      <input
                        type="text"
                        className="custom-dropdown-search-input accordion-search-input"
                        placeholder="Search Sub-TOI"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div
                    className="custom-dropdown-item select-all"
                    onClick={() => {
                      handleSelectAllToggle(filterSubToi, subTois, 'All Sub-TOI', setFilterSubToi, subTois);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="custom-dropdown-checkbox"
                      checked={filterSubToi !== 'All Sub-TOI' && countSelected(filterSubToi, 'All Sub-TOI', subTois) === subTois.length}
                      readOnly
                    />
                    <span>Select all</span>
                  </div>
                  <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
                    {subTois.filter(t => String(t).toLowerCase().includes(filterSearch.toLowerCase())).map(t => {
                      const isChecked = isOptionSelected(filterSubToi, 'All Sub-TOI', t, subTois);
                      return (
                        <div
                          key={t}
                          className="custom-dropdown-item"
                          onClick={() => toggleMultiSelectFilter(filterSubToi, t, 'All Sub-TOI', setFilterSubToi, subTois)}
                        >
                          <input
                            type="checkbox"
                            className="custom-dropdown-checkbox"
                            checked={isChecked}
                            readOnly
                          />
                          <span>{t}</span>
                        </div>
                      );
                    })}
                  </div>
                  {renderBadges(filterSubToi, 'All Sub-TOI', setFilterSubToi, subTois)}
                </AccordionPanel>
            </div>
          )}

          {showSection('submissionDates') && showSubmissionDates && (
            <div className="accordion-item filters-date-range-item">
              <div className="accordion-header filters-date-range-header">
                <div className="accordion-header-left">
                  <Calendar size={18} className="accordion-header-icon" />
                  <span className="accordion-header-title">{submissionDateTitle}</span>
                </div>
                <div className="accordion-header-right filters-date-range-controls">
                  <div className="filters-date-range-picker">
                    <DatePicker
                      selectsRange
                      startDate={tempSubRange ? tempSubRange[0] : null}
                      endDate={tempSubRange ? tempSubRange[1] : null}
                      onChange={(update) => {
                        if (setTempSubRange) setTempSubRange(update);
                        if (!update || (!update[0] && !update[1])) {
                          setSubStart(null);
                          setSubEnd(null);
                          if (setCurrentPage) setCurrentPage(1);
                        }
                      }}
                      onCalendarOpen={() => {
                        if (setTempSubRange) setTempSubRange([subStart, subEnd]);
                      }}
                      minDate={new Date('1951-03-31')}
                      maxDate={new Date()}
                      isClearable
                      placeholderText="MM/DD/YYYY"
                      dateFormat="MM/dd/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={80}
                      dropdownMode="scroll"
                      popperPlacement="bottom-end"
                      shouldCloseOnSelect={false}
                      ref={datePickerRef}
                      customInput={<CustomDateInput />}
                    >
                      <div className="calendar-confirm-bar">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setSubStart(tempSubRange?.[0] || null);
                            setSubEnd(tempSubRange?.[1] || null);
                            if (setCurrentPage) setCurrentPage(1);
                            datePickerRef.current?.setOpen(false);
                          }}
                          disabled={!tempSubRange || !tempSubRange[0]}
                        >
                          Apply Range
                        </button>
                      </div>
                    </DatePicker>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showSection('dispositionDates') && showDispositionDates && (
            <div className="accordion-item filters-date-range-item">
              <div className="accordion-header filters-date-range-header">
                <div className="accordion-header-left">
                  <Calendar size={18} className="accordion-header-icon" />
                  <span className="accordion-header-title">{dispositionDateTitle}</span>
                </div>
                <div className="accordion-header-right filters-date-range-controls">
                  <div className="filters-date-range-picker">
                    <DatePicker
                      selectsRange
                      startDate={tempDispRange ? tempDispRange[0] : null}
                      endDate={tempDispRange ? tempDispRange[1] : null}
                      onChange={(update) => {
                        if (setTempDispRange) setTempDispRange(update);
                        if (!update || (!update[0] && !update[1])) {
                          setDispStart(null);
                          setDispEnd(null);
                          if (setCurrentPage) setCurrentPage(1);
                        }
                      }}
                      onCalendarOpen={() => {
                        if (setTempDispRange) setTempDispRange([dispStart, dispEnd]);
                      }}
                      minDate={new Date('1951-03-31')}
                      maxDate={new Date()}
                      isClearable
                      placeholderText="MM/DD/YYYY"
                      dateFormat="MM/dd/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={80}
                      dropdownMode="scroll"
                      popperPlacement="bottom-end"
                      shouldCloseOnSelect={false}
                      ref={dispDatePickerRef}
                      customInput={<CustomDateInput />}
                    >
                      <div className="calendar-confirm-bar">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setDispStart(tempDispRange?.[0] || null);
                            setDispEnd(tempDispRange?.[1] || null);
                            if (setCurrentPage) setCurrentPage(1);
                            dispDatePickerRef.current?.setOpen(false);
                          }}
                          disabled={!tempDispRange || !tempDispRange[0]}
                        >
                          Apply Range
                        </button>
                      </div>
                    </DatePicker>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="drawer-footer filters-drawer-footer">
          <button
            className="btn btn-sm filters-drawer-btn-reset"
            onClick={() => {
              if (showSection('state')) setFilterState(stateDefault);
              if (showSection('status')) setFilterStatus(statusDefault);
              if (showSection('type')) setFilterType(typeDefault);
              if (showSection('xbrlTag') && propSetFilterXbrlTag) setFilterXbrlTag(xbrlTagDefault);
              if (showSection('toi')) setFilterToi(toiDefault);
              if (showSection('subtoi') && propSetFilterSubToi) setFilterSubToi(subToiDefault);
              if (showSection('company') && !isOurFilings) setFilterCompany(companyDefault);
              if (showSection('response') && setFilterResponse) setFilterResponse(responseDefault);
              if (showSection('submissionDates')) {
                setSubStart(null);
                setSubEnd(null);
                if (setTempSubRange) setTempSubRange([null, null]);
              }
              if (showSection('dispositionDates') && showDispositionDates) {
                setDispStart(null);
                setDispEnd(null);
                if (setTempDispRange) setTempDispRange([null, null]);
              }
            }}
          >
            {resetButtonLabel}
          </button>
          <button
            className="btn btn-sm filters-drawer-btn-apply"
            onClick={() => {
              if (showSection('state')) propSetFilterState(filterState);
              if (showSection('status')) propSetFilterStatus(filterStatus);
              if (showSection('type')) propSetFilterType(filterType);
              if (showSection('xbrlTag') && propSetFilterXbrlTag) propSetFilterXbrlTag(filterXbrlTag);
              if (showSection('toi')) propSetFilterToi(filterToi);
              if (showSection('subtoi') && propSetFilterSubToi) propSetFilterSubToi(filterSubToi);
              if (showSection('company')) propSetFilterCompany(filterCompany);
              if (showSection('response') && propSetFilterResponse) propSetFilterResponse(filterResponse);
              if (showSection('submissionDates')) {
                if (propSetSubStart) propSetSubStart(subStart);
                if (propSetSubEnd) propSetSubEnd(subEnd);
              }
              if (showSection('dispositionDates')) {
                if (propSetDispStart) propSetDispStart(dispStart);
                if (propSetDispEnd) propSetDispEnd(dispEnd);
              }
              if (setCurrentPage) setCurrentPage(1);
              onClose();
            }}
          >
            {applyButtonLabel}
          </button>
        </div>
      </div>
    </>
  );
}
